/**
 * WeakLinkDigestTile — super-admin Weekly OPS tile.
 *
 * Wraps `POST /api/admin/demo-director/weak-link-digest/send-now`
 * to give SEs a Preview + "Send Today" flow for the Monday OPS
 * Slack digest. Preview is dry-run and renders the exact Slack
 * text in a mono `<pre>` block so you can see what the channel
 * will get before firing the webhook.
 *
 * Companion to `SalesIntelTile.jsx`. The two live under a shared
 * "Weekly OPS" subsection of the admin dashboard.
 *
 * Not a Slack oauth integration — this uses the pre-configured
 * OPS_SLACK_WEBHOOK_URL server-side. Missing webhook means a
 * silent "posted: false" response (same as the scheduler).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Megaphone, Eye, Send, AlertTriangle, Target, Trophy, Newspaper, Hash } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function WeakLinkDigestTile() {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState('ops');

  // Fetch the configured Slack channel roster on mount so the
  // picker renders the same options the server recognises. If the
  // fetch fails (403 for non-super-admin, network blip) we fall
  // back to a silent ops-only flow — tile stays functional.
  useEffect(() => {
    let alive = true;
    api.get('/admin/demo-director/slack-channels')
      .then((res) => {
        if (alive && Array.isArray(res?.data?.channels)) {
          setChannels(res.data.channels);
        }
      })
      .catch(() => { /* silent — tile gracefully degrades to ops-only */ });
    return () => { alive = false; };
  }, []);

  const handlePreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      // Preview is channel-agnostic content-wise, but we still
      // pass the channel so the server-side audit trail records
      // which channel the preview was prepared for.
      const res = await api.post(
        `/admin/demo-director/weak-link-digest/send-now?dry_run=true&channel=${encodeURIComponent(channel)}`,
      );
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? 'forbidden' : 'fetch_failed');
      toast.error("Couldn't load digest preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendNow = async () => {
    const chLabel = channels.find((c) => c.name === channel)?.label || channel;
    if (!window.confirm(
      `Post this digest to "${chLabel}" now?\n\nThis fires the webhook immediately — the scheduled Monday run will still happen as usual.`,
    )) return;
    setSending(true);
    try {
      const res = await api.post(
        `/admin/demo-director/weak-link-digest/send-now?dry_run=false&channel=${encodeURIComponent(channel)}`,
      );
      if (res.data.slack_posted) {
        toast.success(`Digest posted to ${chLabel}`);
      } else {
        toast.error(`"${chLabel}" webhook unreachable — check the SLACK_WEBHOOK_${channel.toUpperCase()}_URL env var`);
      }
    } catch (e) {
      toast.error(
        e?.response?.status === 403
          ? 'Super-admin only'
          : 'Send failed — try again',
      );
    } finally {
      setSending(false);
    }
  };

  if (error === 'forbidden') return null;

  // Pretty-print the top whitepaper slug (mirror of _pretty_whitepaper
  // in services/weak_link_digest.py — kept tiny so the tile doesn't
  // need a round-trip just to show a label).
  const prettyWP = (slug) => {
    if (!slug) return null;
    const map = {
      maturity_governance: 'Maturity Governance Playbook',
      lei: 'LEI Methodology Deep Dive',
      lei_methodology: 'LEI Methodology Deep Dive',
      roi_playbook: 'ROI Playbook',
      benchmarking: 'Benchmarking Methodology',
    };
    return map[slug] || slug
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // Disable Send Today when the selected channel is unconfigured
  // (no webhook URL set on the server). The preview still works —
  // composed_text rendering doesn't need a webhook.
  const selectedChannel = channels.find((c) => c.name === channel);
  const channelConfigured = selectedChannel?.configured ?? true;

  return (
    <Card
      data-testid="weak-link-digest-tile"
      className="border-rose-200 bg-gradient-to-br from-white to-rose-50/40"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-rose-500" />
          Monday OPS Digest
          <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-rose-600 border-rose-300 ml-1">
            weekly slack
          </Badge>
        </CardTitle>
        <CardDescription className="mt-0.5">
          Re-engage targets · Wins this week · Top whitepaper. Auto-posts every Monday 08:30 UTC to OPS Slack. Preview or send on demand.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-3 flex-wrap">
          {/* Channel picker — renders only when the server returns
              a roster. Disabled options (unconfigured webhooks)
              still show so the SE sees what's available but can't
              send to something that won't route. Custom native
              <select> keeps the bundle honest — we don't need
              Radix Select for a 4-item dropdown. */}
          {channels.length > 0 && (
            <label
              className="inline-flex items-center gap-1.5 bg-white border border-rose-200 rounded-md pl-2 pr-1"
              data-testid="weak-link-digest-channel-picker"
            >
              <Hash className="h-3 w-3 text-rose-500" />
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="text-[12px] font-medium text-slate-800 bg-transparent focus:outline-none py-1.5 pr-2"
                data-testid="weak-link-digest-channel-select"
              >
                {channels.map((c) => (
                  <option
                    key={c.name}
                    value={c.name}
                    disabled={!c.configured}
                  >
                    {c.label}{c.configured ? '' : ' · not configured'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handlePreview}
            disabled={previewing}
            data-testid="weak-link-digest-preview-btn"
            className="border-rose-300 text-rose-800 hover:bg-rose-100"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            {previewing ? 'Loading…' : preview ? 'Refresh preview' : 'Preview digest'}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSendNow}
            disabled={sending || !preview || !channelConfigured}
            data-testid="weak-link-digest-send-btn"
            className="bg-rose-600 hover:bg-rose-700 text-white disabled:bg-slate-300"
            title={!channelConfigured ? 'Selected channel has no webhook configured' : undefined}
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {sending ? 'Sending…' : 'Send Today'}
          </Button>
        </div>

        {!preview && !previewing && (
          <p className="text-xs text-slate-500" data-testid="weak-link-digest-empty-hint">
            Click <em>Preview</em> to see exactly what Monday&apos;s OPS Slack post will contain.
          </p>
        )}

        {error === 'fetch_failed' && (
          <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="weak-link-digest-error">
            <AlertTriangle className="h-3 w-3" />
            Couldn&apos;t load digest preview. Try again in a moment.
          </p>
        )}

        {preview && (
          <div className="space-y-3" data-testid="weak-link-digest-preview-body">
            {/* Summary stats strip — gives the SE the headline
                numbers at a glance before they read the full post. */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500 flex items-center justify-center gap-1">
                  <Target className="h-2.5 w-2.5" />
                  Re-engage
                </p>
                <p className="text-lg font-bold text-rose-700 tabular-nums">{preview.targets}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500 flex items-center justify-center gap-1">
                  <Trophy className="h-2.5 w-2.5" />
                  Wins
                </p>
                <p className="text-lg font-bold text-emerald-700 tabular-nums">{preview.wins}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-500 flex items-center justify-center gap-1">
                  <Newspaper className="h-2.5 w-2.5" />
                  Paper DLs
                </p>
                <p className="text-lg font-bold text-amber-700 tabular-nums">{preview.whitepaper_downloads}</p>
              </div>
            </div>

            {preview.top_whitepaper && (
              <div
                className="bg-amber-50 border border-amber-200 rounded-md p-3"
                data-testid="weak-link-digest-top-whitepaper"
              >
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                  Top whitepaper this week
                </p>
                <p className="text-[14px] font-bold text-slate-800 mt-0.5">
                  {prettyWP(preview.top_whitepaper)}
                </p>
                <p className="text-[11px] text-slate-600">
                  {preview.whitepaper_downloads} download{preview.whitepaper_downloads === 1 ? '' : 's'} in the past 7 days
                </p>
              </div>
            )}

            {/* Full Slack text preview — mono so whitespace/lines
                render exactly as they will in Slack. Scrollable
                because the post is often 10-20 lines tall. */}
            <div className="bg-slate-900 rounded-md overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-700 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  Slack preview (exact)
                </span>
                <span className="text-[10px] text-slate-500">
                  window: {preview.window_days}d · threshold ≥{preview.threshold}
                </span>
              </div>
              <pre
                className="text-[11px] text-slate-100 font-mono p-3 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap"
                data-testid="weak-link-digest-composed-text"
              >
                {preview.composed_text}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
